import { gql } from '@apollo/client';

/**
 * Join tokens for the copilot's LiveKit room.
 *
 * The backend publishes thirteen `*InstitutionalWeb` agents — Trading,
 * PortfolioAnalytics, Screener, MarketKnowledge, GoalPlanning and the rest —
 * each its own mutation returning the same `LiveKitToken`. They are not
 * thirteen rooms: every one of them resolves to the same per-account web room
 * (the backend rejoins a live room rather than minting another), and what the
 * mutation really chooses is which agent that room should be running. The
 * backend records the choice and the worker switches to it on the next turn.
 *
 * That is why a re-mint must ask for the agent that is active NOW, not for
 * Trading: after the agent hands the conversation to Portfolio Analytics, a
 * reconnect through the Trading mutation would silently hand it back. The
 * provider keeps the active agent and looks its mutation up in
 * `LIVEKIT_WEB_TOKENS`, falling back to Trading for an agent listed nowhere
 * here.
 *
 * Each mutation both mints a token and provisions (or rejoins) the room, so
 * calling one is not free: it is called on connect and on recovery, never on
 * render.
 *
 * Every mutation takes `name` alone. Trading also accepts `isNewSession`, but
 * it is not sent: the backend then resumes the caller's existing thread where
 * it has one, and `isNewRoom` still tells a fresh room from a rejoined one.
 *
 * The token is a LiveKit grant, not our session JWT — it carries `roomJoin`,
 * `canPublish` and `canSubscribe` (data publishing is LiveKit's default), and
 * expires, so a long-lived tab reconnects by asking for another rather than
 * reusing this one.
 */
export const LIVEKIT_TRADING_TOKEN = gql`
  mutation CreateLivekitTradingToken($name: String) {
    createLivekitTokenTradingInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Portfolio Analytics: attribution, risk and exposures across a book. */
export const LIVEKIT_PORTFOLIO_ANALYTICS_TOKEN = gql`
  mutation CreateLivekitPortfolioAnalyticsToken($name: String) {
    createLivekitTokenPortfolioAnalyticsInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Market Knowledge: markets, schemes and securities in general. */
export const LIVEKIT_MARKET_KNOWLEDGE_TOKEN = gql`
  mutation CreateLivekitMarketKnowledgeToken($name: String) {
    createLivekitTokenMarketKnowledgeInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Screener: builds and refines fund and stock screens. */
export const LIVEKIT_SCREENER_TOKEN = gql`
  mutation CreateLivekitScreenerToken($name: String) {
    createLivekitTokenScreenerInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Recommendations: investment portfolio recommendation plans. */
export const LIVEKIT_RECOMMENDATION_TOKEN = gql`
  mutation CreateLivekitRecommendationToken($name: String) {
    createLivekitTokenInvestmentPortfolioRecommendationInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Trade Execution: orders, SIPs and their confirmation steps. */
export const LIVEKIT_TRADE_EXECUTION_TOKEN = gql`
  mutation CreateLivekitTradeExecutionToken($name: String) {
    createLivekitTokenTradeExecutionInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Goal Planning: goals, projections and the plans that fund them. */
export const LIVEKIT_GOAL_PLANNING_TOKEN = gql`
  mutation CreateLivekitGoalPlanningToken($name: String) {
    createLivekitTokenGoalPlanningInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** eKYC: the KYC onboarding flow a handoff can open mid-conversation. */
export const LIVEKIT_EKYC_TOKEN = gql`
  mutation CreateLivekitEkycToken($name: String) {
    createLivekitTokenEkycInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Gold: buying, selling and delivering gold. The backend field is Gold, not GoldBuySell. */
export const LIVEKIT_GOLD_TOKEN = gql`
  mutation CreateLivekitGoldToken($name: String) {
    createLivekitTokenGoldInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Support: customer service, including escalation to a human advisor. */
export const LIVEKIT_CUSTOMER_SERVICE_TOKEN = gql`
  mutation CreateLivekitCustomerServiceToken($name: String) {
    createLivekitTokenCustomerServiceInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/** Policy: insurance policies. */
export const LIVEKIT_POLICY_TOKEN = gql`
  mutation CreateLivekitPolicyToken($name: String) {
    createLivekitTokenPolicyInstitutionalWeb(name: $name) {
      token
      wsUrl
      room
      agentName
      threadName
      isNewRoom
    }
  }
`;

/**
 * Agent type → the mutation that mints a token for it, and the field its
 * grant comes back under.
 *
 * Keyed by the agent type the backend uses (`<role>_web`), which is also what
 * a `CONNECT_AGENT` handoff names once normalised, so the provider can look
 * the active agent up directly. Every agent the app can be handed to has an
 * entry except Advisor: the backend exposes `createLivekitTokenAdvisorInstitutionalWeb`,
 * but its web agent imports modules that do not exist, so a reconnect after a
 * handoff to it re-mints through Trading and the provider shows Trading as
 * the agent answering.
 */
export const LIVEKIT_WEB_TOKENS = {
  trading_web: {
    document: LIVEKIT_TRADING_TOKEN,
    field: 'createLivekitTokenTradingInstitutionalWeb',
  },
  portfolio_analytics_web: {
    document: LIVEKIT_PORTFOLIO_ANALYTICS_TOKEN,
    field: 'createLivekitTokenPortfolioAnalyticsInstitutionalWeb',
  },
  market_knowledge_web: {
    document: LIVEKIT_MARKET_KNOWLEDGE_TOKEN,
    field: 'createLivekitTokenMarketKnowledgeInstitutionalWeb',
  },
  screener_web: {
    document: LIVEKIT_SCREENER_TOKEN,
    field: 'createLivekitTokenScreenerInstitutionalWeb',
  },
  investment_portfolio_recommendation_web: {
    document: LIVEKIT_RECOMMENDATION_TOKEN,
    field: 'createLivekitTokenInvestmentPortfolioRecommendationInstitutionalWeb',
  },
  trade_execution_web: {
    document: LIVEKIT_TRADE_EXECUTION_TOKEN,
    field: 'createLivekitTokenTradeExecutionInstitutionalWeb',
  },
  goal_planning_web: {
    document: LIVEKIT_GOAL_PLANNING_TOKEN,
    field: 'createLivekitTokenGoalPlanningInstitutionalWeb',
  },
  ekyc_web: {
    document: LIVEKIT_EKYC_TOKEN,
    field: 'createLivekitTokenEkycInstitutionalWeb',
  },
  gold_buy_sell_web: {
    document: LIVEKIT_GOLD_TOKEN,
    field: 'createLivekitTokenGoldInstitutionalWeb',
  },
  customer_service_web: {
    document: LIVEKIT_CUSTOMER_SERVICE_TOKEN,
    field: 'createLivekitTokenCustomerServiceInstitutionalWeb',
  },
  policy_web: {
    document: LIVEKIT_POLICY_TOKEN,
    field: 'createLivekitTokenPolicyInstitutionalWeb',
  },
};
